/**
 * Parses the references in an equation.  For example, for this equation
 *
 * T(25,Red Flag,-3p) + I(PTE,26) + TD(Yearly,27,Red Flag,Sum)
 *
 * The results would be
 *
 * <pre>
 * [
 * 		{
 * 			match : 'T(25,scorecards.node.metric.threshold.base,-3p)',
 * 			type : 'metric',
 * 			id : 25,
 * 			series : 'scorecards.node.metric.threshold.base',
 * 			periodNumber : 3,
 * 			periodDirection : 'common.earlier'
 * 		},
 * 		{
 * 			match : 'I(initiatives.calculation.percentTimeElapsed, 26)',
 * 			type : 'initiative',
 * 			id : 26,
 * 			calculation : 'initiatives.calculation.percentTimeElapsed'
 * 		},
 * 		{
 * 			match : 'TD(8,27,scorecards.node.metric.threshold.base,scorecards.node.metric.aggregationType.sum)',
 * 			type : 'td',
 * 			id : 27,
 * 			calendarId : 8,
 * 			series : 'scorecards.node.metric.threshold.base',
 * 			aggregation : 'scorecards.node.metric.aggregationType.sum'
 * 		} ]
 * </pre>
 *
 * The valid types are: metric, threshold, initiative, score, td
 */
import constants from 'scoreboard/components/constants'

import _ from 'lodash'
import EquationParser from 'scoreboard/components/equations/equation-parser'

var patternKeys = [
	'TD_SHORT_PATTERN_STRING',
	'TD_FULL_PATTERN_STRING',
	'METRIC_VALUE_FUNCTION_PATTERN_STRING',
	'THRESHOLD_VALUE_FUNCTION_PATTERN_STRING',
	'INITIATIVE_PATTERN_STRING',
	'SCORE_PATTERN_STRING'
]

// the methods used to process different types of function references.  the
// methods listed in this array correspond to the patterns in the patternKeys array
var methods = [ 'td', 'td', 'metric', 'threshold', 'initiative', 'score' ]

var metricEquationParser = new EquationParser({
	patternKeys: patternKeys,
	methods: methods
})

_.extend(metricEquationParser, {

	metric: function(parsed) {
		return this.series({
			type: 'metric',
			match: parsed[0],
			id: parsed[1] || parsed[3],
			series: constants.ACTUAL_VALUE,
			offset: parsed[2] || parsed[4]
		})
	},

	threshold: function(parsed) {
		return this.series({
			type: 'threshold',
			match: parsed[0],
			id: parsed[1],
			series: parsed[2],
			offset: parsed[3]
		})
	},

	score: function(parsed) {
		return this.series({
			type: 'score',
			match: parsed[0],
			id: Number(parsed[1]),
			offset: parsed[2],
			series: 'scorecards.node.score'
		})
	},

	// constructs correct series information for a metric or threshold
	// reference.  the passed in data should include match, id, series and offset
	series: function(data) {
		var result = {
			match: data.match,
			type: data.type,
			series: data.series || constants.ACTUAL_VALUE
		}
		if (data.id) {
			result.id = Number(data.id)
		}
		// handle offset, if applicable
		var offset = data.offset
		if (offset) {
			offset = Number(offset)
			result.periodNumber = Math.abs(offset)
			if (offset == 0) {
				result.periodDirection = 'common.current'
			}
			if (offset < 0) {
				result.periodDirection = 'common.earlier'
			}
			if (offset > 0) {
				result.periodDirection = 'common.later'
			}
		}
		return result
	},

	initiative: function(parsed) {
		return {
			match: parsed[0],
			type: 'initiative',
			calculation: parsed[1],
			id: Number(parsed[2])
		}
	},

	td: function(parsed) {
		return {
			match: parsed[0],
			type: 'td',
			calendar: parsed[1],
			id: Number(parsed[2]),
			series: _.trim(parsed[3]) || constants.ACTUAL_VALUE,
			aggregation: _.trim(parsed[4]) || constants.AGGREGATIONTYPE_SUM_CODE
		}
	}

})

export default metricEquationParser

metricEquationParser.__filename__ = 'scorecards/metric-equation-parser.js'
